// Types and Data for Attachment Styles survey
// Inspired by Attachment Theory (Bowlby, Ainsworth, Bartholomew) - original implementation for GROWNi.sk

import { v4 as uuidv4 } from 'uuid'
import type { Timestamp } from '../firebase.ts'
import type { UserId } from '../ids.ts'
import { SurveyTypes } from '../Survey.ts'

export type AttachmentSurveyId = string

export interface AttachmentSurvey {
    id: AttachmentSurveyId
    surveyType: SurveyTypes.ATTACHMENT
    userId: UserId
    hash?: string
    questions: AttachmentQuestionType[]
    scores: AttachmentScores
    dominantStyle: AttachmentStyle
    secondaryStyle?: AttachmentStyle
    anxietyScore: number // 0-100 (anxiety dimension)
    avoidanceScore: number // 0-100 (avoidance dimension)
    confidence: number // 0-100
    isDraft?: boolean
    createdAt: Timestamp
    updatedAt?: Timestamp
    deletedAt?: Timestamp | null
}

// Bartholomew's 4 attachment styles (2-dimensional model)
export enum AttachmentStyle {
    SECURE = 'secure', // Istý - low anxiety, low avoidance
    ANXIOUS_PREOCCUPIED = 'anxious_preoccupied', // Úzkostný - high anxiety, low avoidance
    DISMISSIVE_AVOIDANT = 'dismissive_avoidant', // Vyhýbavý odmietavý - low anxiety, high avoidance
    FEARFUL_AVOIDANT = 'fearful_avoidant' // Bojazlivý vyhýbavý - high anxiety, high avoidance
}

export const AttachmentStyleLabels: Record<AttachmentStyle, string> = {
    [AttachmentStyle.SECURE]: 'Istý vzťahový štýl',
    [AttachmentStyle.ANXIOUS_PREOCCUPIED]: 'Úzkostný vzťahový štýl',
    [AttachmentStyle.DISMISSIVE_AVOIDANT]: 'Vyhýbavý odmietavý štýl',
    [AttachmentStyle.FEARFUL_AVOIDANT]: 'Bojazlivý vyhýbavý štýl'
}

export const AttachmentStyleDescriptions: Record<AttachmentStyle, string> = {
    [AttachmentStyle.SECURE]:
        'Cítiš sa pohodlne s blízkosťou aj nezávislosťou. Dôveruješ partnerom a nevyhýbaš sa intimite.',
    [AttachmentStyle.ANXIOUS_PREOCCUPIED]:
        'Túžiš po blízkosti a obávaš sa odmietnutia. Často potrebuješ uistenie o láske partnera.',
    [AttachmentStyle.DISMISSIVE_AVOIDANT]:
        'Ceníš si nezávislosť a vyhýbaš sa blízkosti. Nepohodlne sa cítiš s intimnými emóciami.',
    [AttachmentStyle.FEARFUL_AVOIDANT]: 'Túžiš po blízkosti, ale zároveň sa jej bojíš. Máš zmiešané pocity o vzťahoch.'
}

export interface AttachmentQuestionType {
    id: string
    scenario: string // Relationship scenario
    question: string // Question to answer
    options: AttachmentOption[]
    value?: string // Selected option ID
}

export interface AttachmentOption {
    id: string
    text: string
    style: AttachmentStyle
    anxietyLevel: number // 0-10
    avoidanceLevel: number // 0-10
}

export interface AttachmentScores {
    [AttachmentStyle.SECURE]: number
    [AttachmentStyle.ANXIOUS_PREOCCUPIED]: number
    [AttachmentStyle.DISMISSIVE_AVOIDANT]: number
    [AttachmentStyle.FEARFUL_AVOIDANT]: number
}

// 16 scenarios across different relationship contexts - enriched with emotional depth
export const ATTACHMENT_QUESTIONS: AttachmentQuestionType[] = [
    {
        id: uuidv4(),
        scenario:
            'Je sobota večer. S Petrom ste sa dohodli, že pôjdete na večeru o 19:00. Napísal/a si mu o 17:00, že sa tešíš. Je 20:30 a stále neodpísal. Vidíš, že bol pred hodinou online. Sedíš doma oblečený/á, čakáš.',
        question: 'Čo sa ti teraz odohráva v hlave?',
        options: [
            {
                id: uuidv4(),
                text: 'Asi sa mu niečo stalo alebo má veľa práce - zavolám mu a uvidím',
                style: AttachmentStyle.SECURE,
                anxietyLevel: 1,
                avoidanceLevel: 1
            },
            {
                id: uuidv4(),
                text: 'Prechádzam si naše posledné správy - čo som napísal/a zle? Možno som ho naštvala...',
                style: AttachmentStyle.ANXIOUS_PREOCCUPIED,
                anxietyLevel: 8,
                avoidanceLevel: 2
            },
            {
                id: uuidv4(),
                text: 'Fajn, mám čas pre seba. Pozriem si film, nech sa ozve keď chce',
                style: AttachmentStyle.DISMISSIVE_AVOIDANT,
                anxietyLevel: 2,
                avoidanceLevel: 8
            },
            {
                id: uuidv4(),
                text: 'Určite na mňa zabudol... ale nechcem písať znova, aby som nevyzerala zúfalo',
                style: AttachmentStyle.FEARFUL_AVOIDANT,
                anxietyLevel: 7,
                avoidanceLevel: 6
            }
        ]
    },
    {
        id: uuidv4(),
        scenario:
            'Katka ťa večer chytí za ruku a povie: "Potrebujem sa s tebou porozprávať o nás. O tom, ako sa cítime." V jej hlase cítiš vážnosť. Neviete, či je to dobré alebo zlé znamenie.',
        question: 'Aká je tvoja prvá reakcia?',
        options: [
            {
                id: uuidv4(),
                text: 'Sadneme si a poviem jej, že som otvorený/á - poďme sa porozprávať',
                style: AttachmentStyle.SECURE,
                anxietyLevel: 2,
                avoidanceLevel: 1
            },
            {
                id: uuidv4(),
                text: 'Konečne! Už dávno chcem vedieť, čo si naozaj myslí o nás',
                style: AttachmentStyle.ANXIOUS_PREOCCUPIED,
                anxietyLevel: 7,
                avoidanceLevel: 2
            },
            {
                id: uuidv4(),
                text: 'Cítim, ako sa mi zovrie žalúdok - prečo to musíme rozoberať? Nefunguje to?',
                style: AttachmentStyle.DISMISSIVE_AVOIDANT,
                anxietyLevel: 3,
                avoidanceLevel: 8
            },
            {
                id: uuidv4(),
                text: 'Chcem to počuť, ale čo ak povie niečo, čo nechcem vedieť?',
                style: AttachmentStyle.FEARFUL_AVOIDANT,
                anxietyLevel: 8,
                avoidanceLevel: 6
            }
        ]
    },
    {
        id: uuidv4(),
        scenario:
            'Na firemnom večierku s tebou celý večer flirtuje Martin z vedľajšieho oddelenia. Je vtipný, pozorný, páči sa ti. Na konci večera ti dá číslo a povie: "Chcel by som ťa lepšie spoznať."',
        question: 'Čo cítiš?',
        options: [
            {
                id: uuidv4(),
                text: 'Je mi sympatický, dám tomu šancu a uvidím, kam to povedie',
                style: AttachmentStyle.SECURE,
                anxietyLevel: 2,
                avoidanceLevel: 2
            },
            {
                id: uuidv4(),
                text: 'Konečne niekto, kto ma chce! Napíšem mu ešte dnes večer',
                style: AttachmentStyle.ANXIOUS_PREOCCUPIED,
                anxietyLevel: 8,
                avoidanceLevel: 1
            },
            {
                id: uuidv4(),
                text: 'Je fajn, ale nechcem sa zaviazať - čo ak bude chcieť viac, než viem dať?',
                style: AttachmentStyle.DISMISSIVE_AVOIDANT,
                anxietyLevel: 2,
                avoidanceLevel: 9
            },
            {
                id: uuidv4(),
                text: 'Páči sa mi, ale určite ma sklamá - radšej budem opatrná',
                style: AttachmentStyle.FEARFUL_AVOIDANT,
                anxietyLevel: 9,
                avoidanceLevel: 7
            }
        ]
    },
    {
        id: uuidv4(),
        scenario:
            'Lucia ti povie: "Potrebujem tento víkend sama pre seba. Nepíš mi, nevolaj, len chcem byť chvíľu sama." Posledné týždne ste boli spolu takmer každý deň.',
        question: 'Čo ti prebehne hlavou?',
        options: [
            {
                id: uuidv4(),
                text: 'Rozumiem, každý potrebuje priestor - využijem čas na svoje veci',
                style: AttachmentStyle.SECURE,
                anxietyLevel: 1,
                avoidanceLevel: 1
            },
            {
                id: uuidv4(),
                text: 'Prečo? Čo som urobil/a? Už ma nemiluje? Celý víkend budem premýšľať, čo je zle',
                style: AttachmentStyle.ANXIOUS_PREOCCUPIED,
                anxietyLevel: 9,
                avoidanceLevel: 2
            },
            {
                id: uuidv4(),
                text: 'Fajn, vlastne som aj ja chcel/a trochu priestoru. Nebudeme si na krk',
                style: AttachmentStyle.DISMISSIVE_AVOIDANT,
                anxietyLevel: 1,
                avoidanceLevel: 8
            },
            {
                id: uuidv4(),
                text: 'Bojím sa, že je to začiatok konca... ale nechcem byť zúfalý/á, tak poviem "OK"',
                style: AttachmentStyle.FEARFUL_AVOIDANT,
                anxietyLevel: 8,
                avoidanceLevel: 7
            }
        ]
    },
    {
        id: uuidv4(),
        scenario:
            'Včera večer ste sa s Tomom pohádali. Povedal veci, ktoré ťa zranili. Ráno sa tvári, akoby sa nič nestalo, a pýta sa, čo bude na raňajky.',
        question: 'Ako sa zachováš?',
        options: [
            {
                id: uuidv4(),
                text: 'Poviem mu: "Potrebujem sa vrátiť k včerajšku - zranilo ma to a chcem to vyriešiť"',
                style: AttachmentStyle.SECURE,
                anxietyLevel: 2,
                avoidanceLevel: 1
            },
            {
                id: uuidv4(),
                text: 'Potrebujem vedieť, že ma stále miluje - možno to rýchlo vyjasníme',
                style: AttachmentStyle.ANXIOUS_PREOCCUPIED,
                anxietyLevel: 9,
                avoidanceLevel: 2
            },
            {
                id: uuidv4(),
                text: 'Tiež sa budem tváriť, že sa nič nestalo - nemám energiu na ďalší konflikt',
                style: AttachmentStyle.DISMISSIVE_AVOIDANT,
                anxietyLevel: 3,
                avoidanceLevel: 9
            },
            {
                id: uuidv4(),
                text: 'Chcem to vyriešiť, ale čo ak sa znova pohádame a bude ešte horšie?',
                style: AttachmentStyle.FEARFUL_AVOIDANT,
                anxietyLevel: 8,
                avoidanceLevel: 6
            }
        ]
    },
    {
        id: uuidv4(),
        scenario:
            'Po troch mesiacoch randenia ti Marek prvýkrát povie "Milujem ťa". Je to pri sviečkach, vo chvíli, keď sa na teba díva do očí. Čaká na odpoveď.',
        question: 'Čo cítiš v tej chvíli?',
        options: [
            {
                id: uuidv4(),
                text: 'Ak to cítim tiež, poviem mu to - ak ešte nie, budem úprimná',
                style: AttachmentStyle.SECURE,
                anxietyLevel: 1,
                avoidanceLevel: 1
            },
            {
                id: uuidv4(),
                text: 'Konečne! Tak veľmi som to potrebovala počuť - poviem mu to hneď späť',
                style: AttachmentStyle.ANXIOUS_PREOCCUPIED,
                anxietyLevel: 7,
                avoidanceLevel: 1
            },
            {
                id: uuidv4(),
                text: 'Cítim tlak v hrudi - toto je veľké slovo a ja neviem, či som pripravený/á',
                style: AttachmentStyle.DISMISSIVE_AVOIDANT,
                anxietyLevel: 2,
                avoidanceLevel: 9
            },
            {
                id: uuidv4(),
                text: 'Chcem mu veriť, ale čo ak to tak nemyslí? Alebo sa to časom zmení?',
                style: AttachmentStyle.FEARFUL_AVOIDANT,
                anxietyLevel: 8,
                avoidanceLevel: 7
            }
        ]
    },
    {
        id: uuidv4(),
        scenario:
            'Naplánovali ste si romantický víkend na chate - len vy dvaja, žiadne telefóny, žiadni priatelia. Tri dni sami v horách.',
        question: 'Ako sa na to pripravuješ?',
        options: [
            {
                id: uuidv4(),
                text: 'Teším sa - bude fajn byť spolu bez rozptyľovania',
                style: AttachmentStyle.SECURE,
                anxietyLevel: 1,
                avoidanceLevel: 1
            },
            {
                id: uuidv4(),
                text: 'Nemôžem sa dočkať! Konečne budeme len my dvaja, bez toho, aby mu písali iní',
                style: AttachmentStyle.ANXIOUS_PREOCCUPIED,
                anxietyLevel: 6,
                avoidanceLevel: 1
            },
            {
                id: uuidv4(),
                text: 'Tri dni? Bez prestávky? Trochu ma to stresuje - čo ak si nebudeme mať čo povedať?',
                style: AttachmentStyle.DISMISSIVE_AVOIDANT,
                anxietyLevel: 3,
                avoidanceLevel: 8
            },
            {
                id: uuidv4(),
                text: 'Chcem to, ale čo ak sa ukáže, že spolu nevieme byť tak dlho? Čo ak to bude trápne?',
                style: AttachmentStyle.FEARFUL_AVOIDANT,
                anxietyLevel: 7,
                avoidanceLevel: 6
            }
        ]
    },
    {
        id: uuidv4(),
        scenario:
            'Tvoja najlepšia kamarátka Zuzka ti zavolá v plači. Priateľ ju podviedol a ona nevie, čo má robiť. Je zničená a potrebuje niekoho.',
        question: 'Ako zareaguješ?',
        options: [
            {
                id: uuidv4(),
                text: 'Počúvam, dám jej priestor vyplakať sa a som tu pre ňu - bez súdenia',
                style: AttachmentStyle.SECURE,
                anxietyLevel: 1,
                avoidanceLevel: 1
            },
            {
                id: uuidv4(),
                text: 'Idem za ňou hneď! Musím jej pomôcť, nechcem, aby sa cítila sama',
                style: AttachmentStyle.ANXIOUS_PREOCCUPIED,
                anxietyLevel: 6,
                avoidanceLevel: 2
            },
            {
                id: uuidv4(),
                text: 'Dám jej radu, ale príliš sa do toho emocionálne neťahám - to je jej problém',
                style: AttachmentStyle.DISMISSIVE_AVOIDANT,
                anxietyLevel: 2,
                avoidanceLevel: 8
            },
            {
                id: uuidv4(),
                text: 'Chcem pomôcť, ale neviem, či poviem správnu vec - čo ak to pokazím?',
                style: AttachmentStyle.FEARFUL_AVOIDANT,
                anxietyLevel: 7,
                avoidanceLevel: 6
            }
        ]
    },
    {
        id: uuidv4(),
        scenario:
            'Jana dostala povýšenie a ide to osláviť s kolegyňami. Teba nepozvala. Dozvedel/a si sa to z jej Instagram stories, kde sa smeje s fľašou šampanského.',
        question: 'Aký je tvoj pocit?',
        options: [
            {
                id: uuidv4(),
                text: 'Prajem jej to - má právo oslavovať aj bez mňa, zagratulujem jej potom',
                style: AttachmentStyle.SECURE,
                anxietyLevel: 1,
                avoidanceLevel: 1
            },
            {
                id: uuidv4(),
                text: 'Prečo som tam nebola? Nepatrím medzi jej dôležitých ľudí? To bolí...',
                style: AttachmentStyle.ANXIOUS_PREOCCUPIED,
                anxietyLevel: 8,
                avoidanceLevel: 2
            },
            {
                id: uuidv4(),
                text: 'V pohode, aj tak nemám rada takéto akcie. Jej vec, s kým chce byť',
                style: AttachmentStyle.DISMISSIVE_AVOIDANT,
                anxietyLevel: 2,
                avoidanceLevel: 9
            },
            {
                id: uuidv4(),
                text: 'Cítim sa odstrčený/á, ale nebudem nič hovoriť - nechcem vyzerať needovo',
                style: AttachmentStyle.FEARFUL_AVOIDANT,
                anxietyLevel: 7,
                avoidanceLevel: 7
            }
        ]
    },
    {
        id: uuidv4(),
        scenario:
            'S Adamom ste spolu dva mesiace. Všetko je krásne - smejete sa, rozumiete si, chémia funguje. Začínaš cítiť, že by to mohlo byť vážne.',
        question: 'Čo sa v tebe deje?',
        options: [
            {
                id: uuidv4(),
                text: 'Je to fajn, nechám to plynúť a uvidíme, kam to dôjde',
                style: AttachmentStyle.SECURE,
                anxietyLevel: 2,
                avoidanceLevel: 1
            },
            {
                id: uuidv4(),
                text: 'Chcem vedieť, kde stojíme - mali by sme si povedať, že sme spolu oficálne',
                style: AttachmentStyle.ANXIOUS_PREOCCUPIED,
                anxietyLevel: 7,
                avoidanceLevel: 1
            },
            {
                id: uuidv4(),
                text: 'Možno by som mal/a trochu spomaliť - nechcem sa príliš zaviazať',
                style: AttachmentStyle.DISMISSIVE_AVOIDANT,
                anxietyLevel: 3,
                avoidanceLevel: 9
            },
            {
                id: uuidv4(),
                text: 'Je to krásne, ale čo ak to pokazím? Alebo on stratí záujem?',
                style: AttachmentStyle.FEARFUL_AVOIDANT,
                anxietyLevel: 9,
                avoidanceLevel: 7
            }
        ]
    },
    {
        id: uuidv4(),
        scenario:
            'Zistíš, že tvoj partner Peter si stále píše s bývalou Simonou. Hovoria spolu niekoľkokrát týždenne. Keď sa ho spýtaš, povie: "Sme len priatelia, nič viac."',
        question: 'Ako sa s tým vysporiadaš?',
        options: [
            {
                id: uuidv4(),
                text: 'Verím mu - ak hovorí, že sú len priatelia, prijímam to a rešpektujem',
                style: AttachmentStyle.SECURE,
                anxietyLevel: 2,
                avoidanceLevel: 1
            },
            {
                id: uuidv4(),
                text: 'Potrebujem vedieť viac - o čom sa rozprávajú? Prečo tak často? Naozaj len priatelia?',
                style: AttachmentStyle.ANXIOUS_PREOCCUPIED,
                anxietyLevel: 9,
                avoidanceLevel: 2
            },
            {
                id: uuidv4(),
                text: 'Nechcem to riešiť - každý má právo na priateľstvá, nebudem žiarlivý/á',
                style: AttachmentStyle.DISMISSIVE_AVOIDANT,
                anxietyLevel: 3,
                avoidanceLevel: 8
            },
            {
                id: uuidv4(),
                text: 'Bojím sa, že ma podvádza, ale ak sa sťažujem, vyzerám paranoidne...',
                style: AttachmentStyle.FEARFUL_AVOIDANT,
                anxietyLevel: 8,
                avoidanceLevel: 6
            }
        ]
    },
    {
        id: uuidv4(),
        scenario:
            'Do firmy prišiel nový riaditeľ Michal. Na prvom stretnutí povie: "Chcem vás všetkých osobne spoznať - napíšte mi, čo vás baví, čo vás trápi, aké máte sny." Kolegovia sú nervózni.',
        question: 'Ako sa k tomu postavíš?',
        options: [
            {
                id: uuidv4(),
                text: 'Napíšem mu úprimne - je fajn mať šéfa, ktorý sa zaujíma o ľudí',
                style: AttachmentStyle.SECURE,
                anxietyLevel: 1,
                avoidanceLevel: 1
            },
            {
                id: uuidv4(),
                text: 'Dám si záležať, aby som urobil/a dobrý dojem - možno si ma všimne',
                style: AttachmentStyle.ANXIOUS_PREOCCUPIED,
                anxietyLevel: 7,
                avoidanceLevel: 2
            },
            {
                id: uuidv4(),
                text: 'Napíšem niečo formálne - nepotrebujem, aby šéf vedel o mojich snoch',
                style: AttachmentStyle.DISMISSIVE_AVOIDANT,
                anxietyLevel: 2,
                avoidanceLevel: 8
            },
            {
                id: uuidv4(),
                text: 'Chcem byť otvorený/á, ale čo ak to použije proti mne?',
                style: AttachmentStyle.FEARFUL_AVOIDANT,
                anxietyLevel: 7,
                avoidanceLevel: 7
            }
        ]
    },
    {
        id: uuidv4(),
        scenario:
            'Po roku vzťahu ti Lenka navrhne: "Čo keby sme sa nasťahovali spolu? Mám dosť platiť dva nájmy." Je to logické, ale aj veľký krok.',
        question: 'Čo cítiš?',
        options: [
            {
                id: uuidv4(),
                text: 'Dáva to zmysel - porozprávame sa o tom, čo by to obnášalo',
                style: AttachmentStyle.SECURE,
                anxietyLevel: 2,
                avoidanceLevel: 1
            },
            {
                id: uuidv4(),
                text: 'Konečne! To je dôkaz, že to myslí vážne - hneď poviem áno',
                style: AttachmentStyle.ANXIOUS_PREOCCUPIED,
                anxietyLevel: 6,
                avoidanceLevel: 1
            },
            {
                id: uuidv4(),
                text: 'Cítim tlak v hrudi - stratím svoj priestor, svoju slobodu?',
                style: AttachmentStyle.DISMISSIVE_AVOIDANT,
                anxietyLevel: 3,
                avoidanceLevel: 9
            },
            {
                id: uuidv4(),
                text: 'Chcem to, ale čo ak zistí, aký/á naozaj som a prestane ma mať rád/a?',
                style: AttachmentStyle.FEARFUL_AVOIDANT,
                anxietyLevel: 7,
                avoidanceLevel: 8
            }
        ]
    },
    {
        id: uuidv4(),
        scenario:
            'Sedíte s Matejom v tichom objatí. Cítiš, ako ti bije srdce, ako dýcha, ako sa na teba díva. Je to intímna chvíľa bez slov.',
        question: 'Čo prežívaš?',
        options: [
            {
                id: uuidv4(),
                text: 'Je to krásne - cítim spojenie a pokoj',
                style: AttachmentStyle.SECURE,
                anxietyLevel: 1,
                avoidanceLevel: 1
            },
            {
                id: uuidv4(),
                text: 'Toto je všetko, čo potrebujem - kiež by to trvalo večne',
                style: AttachmentStyle.ANXIOUS_PREOCCUPIED,
                anxietyLevel: 7,
                avoidanceLevel: 1
            },
            {
                id: uuidv4(),
                text: 'Je to trochu intenzívne - potrebujem sa nadýchnuť',
                style: AttachmentStyle.DISMISSIVE_AVOIDANT,
                anxietyLevel: 3,
                avoidanceLevel: 9
            },
            {
                id: uuidv4(),
                text: 'Chcem to, ale cítim sa zraniteľný/á... čo ak ma zraní?',
                style: AttachmentStyle.FEARFUL_AVOIDANT,
                anxietyLevel: 8,
                avoidanceLevel: 8
            }
        ]
    },
    {
        id: uuidv4(),
        scenario:
            'Eva ti pri večeri povie: "Neviem, či si to uvedomuješ, ale keď si pod stresom, si ku mne nepríjemný/á. Dnes si na mňa dvakrát zvýšil/a hlas."',
        question: 'Ako to prijmeš?',
        options: [
            {
                id: uuidv4(),
                text: 'Ďakujem, že mi to povedala - zamyslím sa nad tým a skúsim to zmeniť',
                style: AttachmentStyle.SECURE,
                anxietyLevel: 2,
                avoidanceLevel: 1
            },
            {
                id: uuidv4(),
                text: 'Hneď cítim vinu - určite ma teraz nemá rada, musím to napraviť',
                style: AttachmentStyle.ANXIOUS_PREOCCUPIED,
                anxietyLevel: 9,
                avoidanceLevel: 2
            },
            {
                id: uuidv4(),
                text: 'To je prehnané - mám stres v práci, čo odo mňa čaká?',
                style: AttachmentStyle.DISMISSIVE_AVOIDANT,
                anxietyLevel: 3,
                avoidanceLevel: 9
            },
            {
                id: uuidv4(),
                text: 'Má pravdu a je mi to ľúto... ale čo ak je toto začiatok konca?',
                style: AttachmentStyle.FEARFUL_AVOIDANT,
                anxietyLevel: 8,
                avoidanceLevel: 7
            }
        ]
    },
    {
        id: uuidv4(),
        scenario:
            'Náhodou si všimneš, že tvoj partner/ka rýchlo zatvára telefón, keď prídeš do izby. Stalo sa to už tretíkrát tento týždeň. Na tvoju otázku povie: "Nič, len som si niečo čítal/a."',
        question: 'Čo s tým urobíš?',
        options: [
            {
                id: uuidv4(),
                text: 'Spýtam sa priamo - "Všimol/a som si, že schovávate telefón. Je niečo, o čom by sme mali hovoriť?"',
                style: AttachmentStyle.SECURE,
                anxietyLevel: 2,
                avoidanceLevel: 1
            },
            {
                id: uuidv4(),
                text: 'Nemôžem spať - prečo to robí? Číta si s niekým? Klame mi?',
                style: AttachmentStyle.ANXIOUS_PREOCCUPIED,
                anxietyLevel: 9,
                avoidanceLevel: 2
            },
            {
                id: uuidv4(),
                text: 'Každý má právo na súkromie - nebudem sa do toho starať',
                style: AttachmentStyle.DISMISSIVE_AVOIDANT,
                anxietyLevel: 2,
                avoidanceLevel: 9
            },
            {
                id: uuidv4(),
                text: 'Niečo je zle, cítim to... ale ak sa spýtam, možno to zhorším',
                style: AttachmentStyle.FEARFUL_AVOIDANT,
                anxietyLevel: 8,
                avoidanceLevel: 7
            }
        ]
    }
]

// Fisher-Yates shuffle algorithm
function shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array]
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        const left = shuffled[i]
        const right = shuffled[j]

        if (left === undefined || right === undefined) {
            continue
        }

        shuffled[i] = right
        shuffled[j] = left
    }
    return shuffled
}

// All 16 questions are used
function getQuestionSet(): AttachmentQuestionType[] {
    return shuffleArray(ATTACHMENT_QUESTIONS).map((q) => ({ ...q, value: undefined }))
}

// Initialize a new Attachment survey
export function initializedAttachmentSurvey(userId: UserId): Omit<AttachmentSurvey, 'id' | 'createdAt'> {
    return {
        surveyType: SurveyTypes.ATTACHMENT,
        userId,
        hash: uuidv4(),
        questions: getQuestionSet(),
        scores: {
            [AttachmentStyle.SECURE]: 0,
            [AttachmentStyle.ANXIOUS_PREOCCUPIED]: 0,
            [AttachmentStyle.DISMISSIVE_AVOIDANT]: 0,
            [AttachmentStyle.FEARFUL_AVOIDANT]: 0
        },
        dominantStyle: AttachmentStyle.SECURE,
        anxietyScore: 0,
        avoidanceScore: 0,
        confidence: 0,
        isDraft: true,
        deletedAt: null
    }
}
